import {
  AppTopNaviItemProps
} from "@abb/abb-common-ux-react/components/AppTopNaviItem";
import { MenuItem } from "@abb/abb-common-ux-react/components/Menu";
import useEventListener from "framework/hooks/useEventListener";
import React, { FC, useState } from "react";
import { formUniqueId } from "utilities/identifierUtils";
import { IconTopNavItem, MenuItems, TextTopNavItem } from "./StyledComponents";
import { MenuItemTypes } from "api/models/MenuItemTypes";
import { MenuItemSelection } from "models/menuItemSelection";

export interface NavItemProps {
  activePath?: string;
  menuItem: MenuItemSelection;
  useExternalLinks?: boolean;
  baseUrl: string;
  onSelect: (
    internalRoute: string,
    externalRoute: string | null | undefined,
    isLogOut: boolean
  ) => void;
  translateText?: (text: string) => string;
  isMobile: boolean;
}

export interface NavMenuCompany {
  value?: string | null;
  label: string;
  isDisabled?: boolean;
}

export const NavItem: FC<NavItemProps> = (props: NavItemProps) => {
  const uniqueId = formUniqueId();

  const isIconMenu =
    props.menuItem.menuItemTypeId === MenuItemTypes.UserMenu ||
    props.menuItem.menuItemTypeId === MenuItemTypes.NotificationLink ||
    props.menuItem.menuItemTypeId === MenuItemTypes.ShoppingCartMenu;

  const getTranslation = (text: string) => {
    if (props.translateText) {
      return props.translateText(text);
    }
    return text;
  };

  const subItems =
    props.menuItem.children && props.menuItem.children.length > 0
      ? props.menuItem.children.map((item) => {
        const shouldWorkAsHrefLink =
          (props.useExternalLinks || item.isHrefLink) && !item.isLogOut;
        return (
          <MenuItem
            className={item.isTitleItem ? "MenuItem_Title" : undefined}
            text={getTranslation(item.title ?? "")}
            key={formUniqueId()}
            disabled={item.isTitleItem || item.isDisabled}
            href={shouldWorkAsHrefLink ? `${props.baseUrl}${item.internalRoute}` : undefined}
            selected={props.activePath === item.internalRoute}
            onSelect={
              // When "isLogOut=true", sub item should not work as link. This being the case, we want to trigger the defined onSelect function above.
              shouldWorkAsHrefLink
                ? undefined
                : () => {
                  props.onSelect(item.internalRoute, item.externalLink, item.isLogOut);
                  setIsSubMenuOpen(false);
                }
            }
          />
        );
      })
      : undefined;
  const [isSubMenuOpen, setIsSubMenuOpen] = useState<boolean>(false);
  const hasMenu = subItems !== undefined;
  const shouldDefineOnClick = hasMenu || (!props.useExternalLinks && !props.menuItem.isHrefLink);

  // Handle clicking outside
  // TODO REFACTOR
  const handleClickingOutside = (event: MouseEvent | undefined) => {
    handleClickOutside(event, uniqueId, isSubMenuOpen, setIsSubMenuOpen);
  };
  useEventListener(hasMenu ? document : undefined, "click", (event) => {
    if (hasMenu && isSubMenuOpen) {
      handleClickingOutside(event);
    }
  });

  const isActive = () => {
    if (!hasMenu) {
      return props.activePath === props.menuItem.internalRoute;
    }

    for (const childItem of props.menuItem.children ?? []) {
      if (props.activePath === childItem.internalRoute) {
        return true;
      }
    }

    return false;
  };

  const p: AppTopNaviItemProps = {
    text: isIconMenu && !props.isMobile ? "" : props.menuItem.title,
    id: uniqueId,
    active: isActive(),
    hasMenu: hasMenu,
    disabled: props.menuItem.isDisabled,
    icon: isIconMenu ? getIcon(props.menuItem.menuItemTypeId) : "",
    onClick: shouldDefineOnClick
      ? () => {
        if (hasMenu) {
          setIsSubMenuOpen(!isSubMenuOpen);
        } else {
          props.onSelect(
            props.menuItem.internalRoute,
            props.menuItem.externalLink,
            props.menuItem.isLogOut
          );
        }
      }
      : undefined,
    href: !shouldDefineOnClick ? `${props.baseUrl}${props.menuItem.internalRoute}` : undefined
  };

  let className = hasMenu ? "submenu " : "";
  className += hasMenu && isSubMenuOpen ? " submenu-open" : "";

  return (
    <>
      {!isIconMenu || props.isMobile ? (
        <TextTopNavItem isMobile={props.isMobile} className={className} {...p}>
          {subItems ? <MenuItems $isMobile={props.isMobile} isOpen={isSubMenuOpen}>{subItems}</MenuItems> : null}
        </TextTopNavItem>
      ) : (
        <IconTopNavItem {...p}>
          {subItems ? (
            <MenuItems $isMobile={props.isMobile} alignToParent="right">{isSubMenuOpen ? subItems : null}</MenuItems>
          ) : null}
        </IconTopNavItem>
      )}
    </>
  );
};

// Helpers

const handleClickOutside = (
  event: MouseEvent | undefined,
  id: string,
  isOpen: boolean,
  setValue: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  if (!isOpen) {
    return;
  }
  const target = event?.target as HTMLElement;
  if (isOpen && target && target.offsetParent?.parentElement?.offsetParent?.id === id) {
    return;
  } else if (isOpen && target && target.offsetParent?.id === id) {
    return;
  }
  setValue(false);
};

const getIcon = (menuItemType: MenuItemTypes): string => {
  switch (menuItemType) {
    case MenuItemTypes.UserMenu:
      return "abb/user";
    case MenuItemTypes.NotificationLink:
      return "abb/alarm-bell";
    case MenuItemTypes.ShoppingCartMenu:
      return "abb/cart";
    default:
      return "";
  }
};
